/**
 * Multi-level dock grouping — pure model (no React), so the partition + ordering invariants are
 * unit-tested in the harness. Mirrors the analytics "Split by" stack (data/splitPref): an ORDERED
 * list of group dimensions builds a nested tree — level 0 is the outer grouping, each deeper level
 * sub-groups its parent's rows ("Trade / Company › Priority › Month").
 *
 * Ordering per level reuses the single-level rules the grid already trusted:
 *   · a level carrying an explicit `order` (the canonical workflow dimension, e.g. Punch Status)
 *     → that order first, then any other keys in FIRST-ENCOUNTER order (rows arrive pre-sorted by
 *     rank, so encounter order = operational rank, never alphabetical);
 *   · `sortByKey` (date-bucket dimensions) → keys ascending (chronological);
 *   · otherwise → group size descending, key ascending as a deterministic tie-break.
 * Rows inside a leaf keep their incoming (already-sorted) order.
 */

/** Max group depth — state, the picker, and persistence all read this. Matches the Split-by cap. */
export const MAX_GROUP_LEVELS = 4;

/** Composite path separator (a control char no group value can contain) — keys collapse state. */
const GROUP_PATH_SEP = String.fromCharCode(1);

/** One active grouping level: its option key, display label, value accessor, and ordering rule. */
export interface GroupLevel<Row> {
  key: string;
  label: string;
  keyOf: (r: Row) => string;
  /** Explicit display order for this level's group values (the canonical dimension only). */
  order?: string[];
  /** Order this level's group values ascending by key string (chronological date buckets). */
  sortByKey?: boolean;
}

/** A node in the grouping tree. `rows` are ALL rows under the node (this level + descendants);
 *  `children` is null at the deepest active level (a leaf, whose `rows` render directly). */
export interface GroupNode<Row> {
  key: string;     // the group VALUE at this level (e.g. "Drywall")
  label: string;   // the DIMENSION label (e.g. "Trade / Company")
  depth: number;   // 0-based nesting depth
  path: string;    // ancestor values + this value, joined by GROUP_PATH_SEP — stable collapse key
  rows: Row[];     // every row in this node, in incoming order
  children: GroupNode<Row>[] | null;
}

/** Checkbox semantics for the group picker (pure, mirrors splitPref.toggleSplit):
 *   · unchecked → APPEND as the deepest level;
 *   · unchecked while already at `max` → REPLACE the deepest level;
 *   · checked → REMOVE it; deeper levels PROMOTE up one (array filter keeps order). */
export function toggleGroup(groups: string[], key: string, max = MAX_GROUP_LEVELS): string[] {
  if (groups.includes(key)) return groups.filter(g => g !== key);
  if (groups.length >= max) return [...groups.slice(0, max - 1), key];
  return [...groups, key];
}

/** The ONE rules gate for a group stack: accepts a legacy single string OR an array, keeps only
 *  keys the current config can express, deduped in order, capped at `max`. ('__none' / '' → []). */
export function sanitizeGroupBy(raw: unknown, valid: ReadonlySet<string>, max = MAX_GROUP_LEVELS): string[] {
  const arr = Array.isArray(raw)
    ? raw
    : (typeof raw === 'string' && raw && raw !== '__none' ? [raw] : []);
  const out: string[] = [];
  for (const v of arr) {
    if (typeof v !== 'string' || !valid.has(v) || out.includes(v)) continue;
    out.push(v);
    if (out.length >= max) break;
  }
  return out;
}

function orderGroupKeys<Row>(m: Map<string, Row[]>, encounter: string[], lvl: GroupLevel<Row>): string[] {
  if (lvl.order) {
    const known = lvl.order.filter(k => m.has(k));
    const rest = encounter.filter(k => !lvl.order!.includes(k));
    return [...known, ...rest];
  }
  if (lvl.sortByKey) return [...encounter].sort();
  return [...encounter].sort((a, b) => (m.get(b)!.length - m.get(a)!.length) || a.localeCompare(b));
}

/** Build the nested grouping tree from pre-sorted rows and the active group levels (depth order).
 *  Returns [] for an empty `levels` — callers render a flat list in that case. */
export function buildGroupTree<Row>(
  rows: Row[], levels: GroupLevel<Row>[], depth = 0, parentPath = '',
): GroupNode<Row>[] {
  if (depth >= levels.length) return [];
  const lvl = levels[depth];
  const m = new Map<string, Row[]>();
  const encounter: string[] = [];
  for (const r of rows) {
    const k = lvl.keyOf(r) || '—';
    let bucket = m.get(k);
    if (!bucket) { bucket = []; m.set(k, bucket); encounter.push(k); }
    bucket.push(r);
  }
  const isLeaf = depth + 1 >= levels.length;
  return orderGroupKeys(m, encounter, lvl).map(k => {
    const childRows = m.get(k)!;
    const path = parentPath ? parentPath + GROUP_PATH_SEP + k : k;
    return {
      key: k, label: lvl.label, depth, path, rows: childRows,
      children: isLeaf ? null : buildGroupTree(childRows, levels, depth + 1, path),
    };
  });
}

/** Visible leaf rows in render order — only rows whose every ancestor (and own leaf) path is
 *  expanded, each leaf paged to `shown`. The grid's renderer walks the tree the SAME way, so the
 *  indices here line up with the on-screen row numbers / roving tabindex. */
export function visibleLeafRows<Row>(nodes: GroupNode<Row>[], expanded: Set<string>, shown: number): Row[] {
  const out: Row[] = [];
  const walk = (ns: GroupNode<Row>[]) => {
    for (const n of ns) {
      if (!expanded.has(n.path)) continue;
      if (n.children) walk(n.children);
      else out.push(...n.rows.slice(0, shown));
    }
  };
  walk(nodes);
  return out;
}
